import { setIntegrationData } from '@/atoms/integrationAtoms';
import { stripSectionMarkers } from '@/components/tiptap/shared/section-markers';
import { useLatestRef } from '@/hooks/useLatestRef';
import {
  type MedicalDirectorSidebar,
  PropertyBagValue,
} from '@/integrations/medical-director/medicalDirectorSidebar';
import {
  trackIntegrationDataRetrieved,
  trackIntegrationPatientSelected,
  trackNoteSynced,
} from '@/lib/analytics/scribe/integrations';
import { isValidEHRPatientId } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { generateNoteAppendix } from '../generateNoteAppendix';
import {
  type MedicalDirectorPractitionerDetails,
  type MedicalDirectorSidebarDemographics,
  type MedicalDirectorSidebarEvent,
} from '../types';
import { type MdSession } from './types';
import { type PendingMdPatient } from './usePendingMdPatient';

type IntegrationData = {
  patientId?: string;
  ehrProvider?: string;
};

type UseMdCallbacksParams = {
  medicalDirector: MedicalDirectorSidebar | null;
  pendingPatient: PendingMdPatient;
  session: MdSession;
  practitionerDetails: MedicalDirectorPractitionerDetails | null;
  setPractitionerDetails: (data: MedicalDirectorPractitionerDetails) => void;
  isRunning: boolean;
  isUpdatingPatientDetails: boolean;
  integrationData: IntegrationData;
  updatePatientDetails: (params: {
    sessionId: string;
    patientDetails: {
      name: string;
      dob: string | undefined;
      gender: string;
      demographic_details: string;
    };
  }) => void;
};

export type MdCallbacks = {
  processPractitionerResponse: (data: MedicalDirectorPractitionerDetails) => void;
  processDemographicsResponse: (data: MedicalDirectorSidebarDemographics) => void;
  onSwitchPatientCallback: (newPatientId: string) => void;
  onCheckCurrentPatientCallback: () => string | undefined;
  onPushNotesCallback: (notes: string) => void;
  mdEventHandler: (event: MedicalDirectorSidebarEvent) => void;
};

export const useMdCallbacks = ({
  medicalDirector,
  pendingPatient,
  session,
  practitionerDetails,
  setPractitionerDetails,
  isRunning,
  isUpdatingPatientDetails,
  integrationData,
  updatePatientDetails,
}: UseMdCallbacksParams): MdCallbacks => {
  const intl = useIntl();

  // Localised toast payloads. Memoised on `intl` (which is stable across
  // renders for a given locale) so callers using these in dep arrays don't
  // see spurious churn.
  const recordingInProgressSwitchToast = useMemo(
    () => ({
      tone: 'destructive' as const,
      title: intl.formatMessage({
        defaultMessage: 'Recording in progress',
        id: 'uX0nCpyyHj',
        description:
          'Toast title when the user tries to switch patients in MD while a recording is in progress.',
      }),
      description: intl.formatMessage({
        defaultMessage: 'Please end recording before switching patients.',
        id: 'Eo5Qaw9ZXT',
        description:
          'Toast description when the user tries to switch patients in MD while a recording is in progress.',
      }),
    }),
    [intl]
  );

  const recordingInProgressCloseToast = useMemo(
    () => ({
      tone: 'destructive' as const,
      title: intl.formatMessage({
        defaultMessage: 'Recording in progress',
        id: 'w/xDc0lvAk',
        description:
          'Toast title when MD reports the clinical window has closed while a recording is in progress.',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'You need to stop this recording before closing the current patient.',
        id: 'GfEbf7BAO8',
        description:
          'Toast description when MD reports the clinical window has closed while a recording is in progress.',
      }),
    }),
    [intl]
  );

  const noPatientSelectedToast = useMemo(
    () => ({
      tone: 'destructive' as const,
      title: intl.formatMessage({
        defaultMessage: 'No patient selected',
        id: '/HLMMrTSBv',
        description:
          'Toast title when MD reports the clinical window has closed but no patient is open.',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'You need to select a patient before closing the current patient.',
        id: 'KHa1CCLMtK',
        description:
          'Toast description when MD reports the clinical window has closed but no patient is open.',
      }),
    }),
    [intl]
  );
  const isRunningRef = useLatestRef(isRunning);
  const integrationDataRef = useLatestRef(integrationData);
  const sessionRef = useLatestRef(session);

  const processPractitionerResponse = useCallback(
    (data: MedicalDirectorPractitionerDetails) => {
      setPractitionerDetails(data);
    },
    [setPractitionerDetails]
  );

  const processDemographicsResponse = useCallback(
    (data: MedicalDirectorSidebarDemographics) => {
      // Extract to a local const so TypeScript narrows `string | undefined`
      // to `string` after the early-return guard (mutable ref properties
      // aren't narrowable through control flow).
      const currentSessionId = sessionRef.current?.session_id;
      if (
        !currentSessionId ||
        (sessionRef.current?.patient?.name ?? '').trim().length > 0 ||
        isUpdatingPatientDetails
      ) {
        return;
      }

      const nameParts = [
        data.Demographics.FirstName,
        data.Demographics.MiddleName,
        data.Demographics.Surname,
      ].filter(Boolean);
      const name = nameParts.join(' ');
      const dob = data.Demographics.Dob.split('T').at(0);
      const gender = data.Demographics.GenderCode;

      logger.debug(
        '[MD]',
        'Updating session patient details for session',
        currentSessionId
      );

      updatePatientDetails({
        sessionId: currentSessionId,
        patientDetails: {
          name,
          dob,
          gender,
          demographic_details: `${name} ${dob} ${gender}`,
        },
      });
      logger.debug('[MD]', 'Patient demographics response sent');
      trackIntegrationDataRetrieved({
        integration_type: 'medical_director',
        data_type: 'demographics',
      });
    },
    [updatePatientDetails, isUpdatingPatientDetails, sessionRef]
  );

  const onSwitchPatientCallback = useCallback(
    (newPatientId: string) => {
      if (!medicalDirector) return;

      logger.debug('[MD]', 'Switching patient', newPatientId);

      if (!isValidEHRPatientId(Number(newPatientId))) return;

      if (isRunningRef.current) {
        const currentPatientId =
          integrationDataRef.current.patientId ||
          medicalDirector.readPropertyBagValue(PropertyBagValue.CURRENT_PATIENT_ID);

        if (String(currentPatientId) !== String(newPatientId)) {
          toast(recordingInProgressSwitchToast);
          return;
        }
      }

      pendingPatient.set(String(newPatientId));
      medicalDirector.openPatient(newPatientId);

      setIntegrationData({
        patientId: String(newPatientId),
      });
      logger.debug('[MD]', 'Patient switched');
      trackIntegrationPatientSelected({
        integration_type: 'medical_director',
        patient_id: newPatientId,
      });
    },
    [
      integrationDataRef,
      isRunningRef,
      medicalDirector,
      pendingPatient,
      recordingInProgressSwitchToast,
    ]
  );

  const onCheckCurrentPatientCallback = useCallback(() => {
    if (!medicalDirector) return;

    logger.debug('[MD]', 'Read current patient from MD property bag');
    return medicalDirector.readPropertyBagValue(PropertyBagValue.CURRENT_PATIENT_ID);
  }, [medicalDirector]);

  const onPushNotesCallback = useCallback(
    (notes: string) => {
      if (!medicalDirector) return;

      logger.debug('[MD]', 'Push note callback');
      const patientId =
        integrationDataRef.current.patientId ||
        medicalDirector.readPropertyBagValue(PropertyBagValue.CURRENT_PATIENT_ID);

      if (
        integrationDataRef.current.patientId === undefined &&
        isValidEHRPatientId(patientId)
      ) {
        logger.debug('[MD]', 'Setting valid patientId to replace undefined');
        setIntegrationData({
          patientId: String(patientId),
        });

        if (!isUpdatingPatientDetails && !session?.patient?.name) {
          medicalDirector.send('GetDemographicsRequest', {
            PatientID: Number(patientId),
          });
        }
      }

      if (!isValidEHRPatientId(patientId)) return;

      logger.debug('[MD]', 'Pushing to patient progress note');
      // sessionRef mirrors `session` synchronously via useLatestRef, so the
      // `|| session?.is_consent` fallback is unreachable. Reading only via
      // the ref also avoids forcing this callback to rebuild on consent
      // changes.
      const append = generateNoteAppendix({
        practitionerDetails,
        isConsent: sessionRef.current?.is_consent,
        intl,
      });

      medicalDirector.addProgressNote(
        Number(patientId),
        '\n\n' + stripSectionMarkers(notes) + '\n\n' + append + '\n\n'
      );

      if (sessionRef.current?.session_id) {
        trackNoteSynced({
          session_id: sessionRef.current.session_id,
          ehr_provider: 'medical_director',
        });
      }
    },
    [
      intl,
      medicalDirector,
      practitionerDetails,
      isUpdatingPatientDetails,
      session?.patient,
      integrationDataRef,
      sessionRef,
    ]
  );

  const mdEventHandler = useCallback(
    (data: MedicalDirectorSidebarEvent) => {
      const eventName = data.Topic.Name;
      logger.debug('[MD]', 'MD event handler', eventName);

      const isPatientChangeEvent = [
        'hcn.md3.clinicalwindow.ready',
        'hcn.md3.currentpatient.changed',
      ].includes(eventName);

      if (isPatientChangeEvent) {
        const parameter =
          typeof data.Parameter === 'object' ? data.Parameter.at(0) : data.Parameter;

        if (
          isValidEHRPatientId(parameter) &&
          integrationDataRef.current.patientId !== String(parameter)
        ) {
          logger.debug(
            '[MD]',
            `attempting to switch to patient ${parameter} when opened ${integrationDataRef.current.patientId}`
          );
          if (isRunningRef.current) {
            pendingPatient.clear();
            if (medicalDirector && integrationDataRef.current.patientId) {
              medicalDirector.openPatient(integrationDataRef.current.patientId);
            }
            toast(recordingInProgressSwitchToast);
            return;
          }

          pendingPatient.set(String(parameter));
          setIntegrationData({
            patientId: String(parameter),
          });
        }
      }

      if (medicalDirector && eventName === 'hcn.md3.clinicalwindow.closed') {
        logger.debug('[MD]', 'Clinical window closed');
        const currentPatientId = medicalDirector.readPropertyBagValue(
          PropertyBagValue.CURRENT_PATIENT_ID
        );

        logger.debug(
          '[MD]',
          'Clinical window closed with current patient',
          currentPatientId
        );

        const noPatientOpen =
          !currentPatientId || currentPatientId.length === 0 || currentPatientId === '""';

        if (!noPatientOpen) return;

        if (!isRunningRef.current) {
          setIntegrationData({ patientId: undefined });
        } else if (integrationDataRef.current.patientId) {
          medicalDirector.openPatient(integrationDataRef.current.patientId);
          toast(recordingInProgressCloseToast);
        } else {
          toast(noPatientSelectedToast);
        }
      }
    },
    [
      medicalDirector,
      pendingPatient,
      integrationDataRef,
      isRunningRef,
      recordingInProgressSwitchToast,
      recordingInProgressCloseToast,
      noPatientSelectedToast,
    ]
  );

  return {
    processPractitionerResponse,
    processDemographicsResponse,
    onSwitchPatientCallback,
    onCheckCurrentPatientCallback,
    onPushNotesCallback,
    mdEventHandler,
  };
};
